import { useState } from 'react'
import { DemoFrame } from '@/components/primitives'

type DraftState = 'pending' | 'approved' | 'changes'

export function NexExchange() {
  const [draft, setDraft] = useState<DraftState>('pending')

  return (
    <DemoFrame
      title="Nex · assist session"
      tone="nex"
      tag="Draft created. Review required before publication."
      ariaLabel="Stylized Nex session: a grounded answer citing product knowledge, then a workflow draft requiring human review. Synthetic data."
    >
      <div className="p-4 pb-16 sm:p-6 sm:pb-16">
        {/* Question */}
        <div className="flex justify-end">
          <p className="max-w-md rounded-sm border border-base-linebright bg-base-800 px-4 py-3 text-sm text-ink">
            Why didn&rsquo;t contact #48213 dial this morning?
          </p>
        </div>

        {/* Grounded answer */}
        <div className="mt-4 max-w-xl rounded-sm border border-nex/30 bg-nex/5 px-4 py-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-nex-soft">
            Nex · grounded answer
          </p>
          <p className="mt-2.5 text-sm leading-relaxed text-ink">
            Campaign <span className="font-semibold">FL Renewal Outreach</span> evaluates consent,
            DNC state, and calling-window rules before staging work. Contact #48213 was reserved at
            09:12, then held: the configured Florida calling window opens at 10:00 local time. The
            record enters the hopper when the window opens. No rule failed.
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Sources cited by Nex">
            <li className="chip-nex">Enchiridion · hoppers and calling windows</li>
            <li className="chip-nex">account context · campaign FL-REN-02</li>
            <li className="chip-neutral">permissions · workflow:read</li>
          </ul>
        </div>

        {/* Draft */}
        <div className="mt-4 rounded-sm border border-base-line bg-base-950/80 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-ink">
              Workflow draft v0.1: route renewal contacts to licensed queues
            </p>
            <span className="chip-nex">created by Nex</span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-ink-mute">
            Trigger: contact created. Condition: state + consent + window. Action: route to
            licensed queue. Drafted from the answer above, inside your account scope and
            permissions.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-base-line pt-4">
            {draft === 'pending' && (
              <>
                <button
                  type="button"
                  onClick={() => setDraft('approved')}
                  className="btn-secondary py-2"
                >
                  Review and approve
                </button>
                <button
                  type="button"
                  onClick={() => setDraft('changes')}
                  className="btn-ghost px-0"
                >
                  Request changes
                </button>
              </>
            )}
            {draft === 'approved' && <span className="chip-good">published as v1.0 · by ops.lead</span>}
            {draft === 'changes' && (
              <>
                <span className="chip-warn">returned to Nex for revision</span>
                <button type="button" onClick={() => setDraft('pending')} className="btn-ghost px-0">
                  Restore draft
                </button>
              </>
            )}
            <span className="text-xs text-ink-dim" role="status" aria-live="polite">
              {draft === 'pending' && 'A human with publish permission decides what happens next.'}
              {draft === 'approved' && 'The approved version executes; the draft stays in history.'}
              {draft === 'changes' && 'Nex revises drafts. It does not publish them.'}
            </span>
          </div>
        </div>

        <p className="mt-4 text-xs text-ink-dim">
          Nex works inside account context, configured knowledge, permissions, and review
          boundaries. It drafts supported changes; humans review and publish.
        </p>
      </div>
    </DemoFrame>
  )
}
